import React, { type CSSProperties } from 'react';
import {
    ArrowRight,
    Award,
    BadgeCheck,
    CheckCircle2,
    ExternalLink,
    Gauge,
    Globe2,
    Laptop,
    MessageCircle,
    Rocket,
    Search,
    ShieldCheck,
    Smartphone,
    Sparkles,
    Star,
    TrendingUp,
    Users,
} from 'lucide-react';
import {
    serviceAdvocateIconTokens,
    serviceAdvocateTokens,
    type ServiceAdvocateColorToken,
    type ServiceAdvocateIconToken,
} from './ServiceAdvocate.tokens';

export type ServiceAdvocateLinkComponent = React.ComponentType<{
    href: string;
    children?: React.ReactNode;
    className?: string;
    title?: string;
    target?: string;
    rel?: string;
    'aria-label'?: string;
    [key: string]: any;
}>;

export type ServiceAdvocateIcon = ServiceAdvocateIconToken | React.ReactNode | React.ElementType;

export type ServiceAdvocateImage = {
    src?:
        | string
        | {
              src?: string;
              width?: number;
              height?: number;
              [key: string]: unknown;
          };
    url?: string;
    original?: string;
    thumbnail?: string;
    alt?: string;
    title?: string;
    width?: number;
    height?: number;
    objectPosition?: string;
    loading?: 'eager' | 'lazy';
    decoding?: 'async' | 'auto' | 'sync';
    decorative?: boolean;
};

export type ServiceAdvocateFeature = {
    id?: string | number;
    title: React.ReactNode;
    description?: React.ReactNode;
    icon?: ServiceAdvocateIcon;
    iconSrc?: string;
    iconAlt?: string;
    accentColor?: string;
    accentColour?: string;
    accentSoftColor?: string;
    accentSoftColour?: string;
};

export type ServiceAdvocateProofLogo = 'google' | React.ReactNode | ServiceAdvocateImage | false;

export type ServiceAdvocateProof = {
    logo?: ServiceAdvocateProofLogo;
    rating?: React.ReactNode;
    title?: React.ReactNode;
    description?: React.ReactNode;
    showStars?: boolean;
    starCount?: number;
    href?: string;
    ariaLabel?: string;
    titleAttribute?: string;
    target?: string;
    rel?: string;
};

export type ServiceAdvocateTestimonial = {
    quote?: React.ReactNode;
    authorName?: React.ReactNode;
    authorRole?: React.ReactNode;
    avatar?: string | ServiceAdvocateImage | false;
    avatarAlt?: string;
};

export type ServiceAdvocateChecklistObject = {
    id?: string | number;
    text: React.ReactNode;
    icon?: ServiceAdvocateIcon;
};

export type ServiceAdvocateChecklistItem = React.ReactNode | ServiceAdvocateChecklistObject;

export type ServiceAdvocateAction = {
    label: React.ReactNode;
    href: string;
    ariaLabel?: string;
    title?: string;
    target?: string;
    rel?: string;
    icon?: ServiceAdvocateIcon;
    iconSrc?: string;
    iconAlt?: string;
};

export type ServiceAdvocateColors = Partial<Record<ServiceAdvocateColorToken, string>>;

export type ServiceAdvocateMedia = React.ReactNode | ServiceAdvocateImage | false;

export type ServiceAdvocateProps = {
    eyebrow?: React.ReactNode;
    title?: React.ReactNode;
    description?: React.ReactNode;
    features?: ServiceAdvocateFeature[];
    media?: ServiceAdvocateMedia;
    proof?: ServiceAdvocateProof | false;
    testimonial?: ServiceAdvocateTestimonial | false;
    checklist?: ServiceAdvocateChecklistItem[] | false;
    primaryAction?: ServiceAdvocateAction | false;
    secondaryAction?: ServiceAdvocateAction | false;
    LinkComponent?: ServiceAdvocateLinkComponent;
    Link?: ServiceAdvocateLinkComponent;
    titleComponent?: 'h1' | 'h2' | 'h3' | 'div';
    accentColor?: string;
    accentColour?: string;
    accentDarkColor?: string;
    accentDarkColour?: string;
    accentSoftColor?: string;
    accentSoftColour?: string;
    backgroundColor?: string;
    backgroundColour?: string;
    surfaceColor?: string;
    surfaceColour?: string;
    textColor?: string;
    textColour?: string;
    textSoftColor?: string;
    textSoftColour?: string;
    textMutedColor?: string;
    textMutedColour?: string;
    colors?: ServiceAdvocateColors;
    colours?: ServiceAdvocateColors;
    maxWidth?: number | string;
    id?: string;
    className?: string;
    style?: CSSProperties;
    ariaLabel?: string;
};

type ServiceAdvocateCssVars = CSSProperties &
    Record<`--service-advocate-${string}`, string | number | undefined>;

type NormalizedImage = {
    src: string;
    alt: string;
    title?: string;
    width?: number;
    height?: number;
    objectPosition?: string;
    loading: 'eager' | 'lazy';
    decoding: 'async' | 'auto' | 'sync';
    decorative: boolean;
};

const t = serviceAdvocateTokens;
const iconTokenSet = new Set<string>(serviceAdvocateIconTokens);

const iconRegistry: Record<ServiceAdvocateIconToken, React.ElementType> = {
    arrowRight: ArrowRight,
    award: Award,
    badgeCheck: BadgeCheck,
    checkCircle: CheckCircle2,
    externalLink: ExternalLink,
    gauge: Gauge,
    globe: Globe2,
    laptop: Laptop,
    messageCircle: MessageCircle,
    rocket: Rocket,
    search: Search,
    shieldCheck: ShieldCheck,
    smartphone: Smartphone,
    sparkles: Sparkles,
    star: Star,
    trendingUp: TrendingUp,
    users: Users,
};

const serviceAdvocateCss = `
.service-advocate {
  --service-advocate-bg: ${t.colors.background};
  --service-advocate-surface: ${t.colors.surface};
  --service-advocate-surface-soft: ${t.colors.surfaceSoft};
  --service-advocate-text: ${t.colors.text};
  --service-advocate-text-soft: ${t.colors.textSoft};
  --service-advocate-text-muted: ${t.colors.textMuted};
  --service-advocate-accent: ${t.colors.accent};
  --service-advocate-accent-dark: ${t.colors.accentDark};
  --service-advocate-accent-soft: ${t.colors.accentSoft};
  --service-advocate-accent-wash: ${t.colors.accentWash};
  --service-advocate-accent-contrast: ${t.colors.accentContrast};
  --service-advocate-border: ${t.colors.border};
  --service-advocate-border-strong: ${t.colors.borderStrong};
  --service-advocate-shadow: ${t.colors.shadow};
  --service-advocate-soft-shadow: ${t.colors.softShadow};
  --service-advocate-max-width: ${t.sizes.maxWidth}px;
  width: 100%;
  padding: ${t.spacing.sectionY}px ${t.spacing.sectionX}px;
  background: var(--service-advocate-bg);
  color: var(--service-advocate-text);
  font-family: ${t.typography.fontFamily};
  box-sizing: border-box;
  container-type: inline-size;
}
.service-advocate,
.service-advocate * {
  box-sizing: border-box;
}
.service-advocate-shell {
  width: 100%;
  max-width: var(--service-advocate-max-width);
  margin: 0 auto;
  overflow: hidden;
  border: 1px solid var(--service-advocate-border-strong);
  border-radius: ${t.radius.shell}px;
  background:
    radial-gradient(circle at 85% 17%, var(--service-advocate-accent-wash), transparent 24%),
    linear-gradient(180deg, rgba(255, 255, 255, 0.97), rgba(255, 255, 255, 0.92)),
    var(--service-advocate-surface);
  box-shadow: var(--service-advocate-shadow);
}
.service-advocate-top {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(240px, ${t.sizes.mediaWidth}px);
  align-items: center;
  gap: ${t.spacing.topGap}px;
  padding: ${t.spacing.shellPadding}px ${t.spacing.shellPadding}px 20px;
}
.service-advocate[data-has-media="false"] .service-advocate-top {
  grid-template-columns: minmax(0, 1fr);
}
.service-advocate-content {
  min-width: 0;
  max-width: ${t.sizes.contentMaxWidth}px;
}
.service-advocate-eyebrow {
  display: inline-flex;
  max-width: 100%;
  align-items: center;
  gap: 10px;
  margin: 0 0 14px;
  color: var(--service-advocate-accent);
  font-size: ${t.typography.eyebrow.fontSize}px;
  line-height: ${t.typography.eyebrow.lineHeight};
  font-weight: ${t.typography.eyebrow.fontWeight};
  letter-spacing: ${t.typography.eyebrow.letterSpacing};
  text-transform: ${t.typography.eyebrow.textTransform};
  overflow-wrap: anywhere;
}
.service-advocate-eyebrow::before {
  content: "";
  display: block;
  width: 34px;
  height: 2px;
  flex: 0 0 auto;
  border-radius: 999px;
  background: currentColor;
}
.service-advocate-title {
  max-width: 100%;
  margin: 0;
  color: var(--service-advocate-text);
  font-size: ${t.typography.title.fontSize}px;
  line-height: ${t.typography.title.lineHeight};
  font-weight: ${t.typography.title.fontWeight};
  letter-spacing: ${t.typography.title.letterSpacing};
  overflow-wrap: anywhere;
}
.service-advocate-description {
  max-width: 760px;
  margin: 12px 0 0;
  color: var(--service-advocate-text-soft);
  font-size: ${t.typography.description.fontSize}px;
  line-height: ${t.typography.description.lineHeight};
  font-weight: ${t.typography.description.fontWeight};
  letter-spacing: ${t.typography.description.letterSpacing};
  overflow-wrap: anywhere;
}
.service-advocate-media {
  position: relative;
  display: flex;
  min-width: 0;
  min-height: ${t.sizes.mediaMinHeight}px;
  align-items: center;
  justify-content: center;
}
.service-advocate-media > * {
  max-width: 100%;
}
.service-advocate-media img {
  display: block;
  max-width: 100%;
  height: auto;
  object-fit: contain;
  object-position: var(--service-advocate-media-position, center);
}
.service-advocate-device {
  position: relative;
  width: min(100%, 360px);
  min-height: 190px;
}
.service-advocate-device-laptop {
  position: absolute;
  top: 16px;
  left: 0;
  width: 82%;
  border: 4px solid #0b1220;
  border-radius: 8px 8px 4px 4px;
  background: #0b1220;
  box-shadow: 0 18px 32px rgba(7, 20, 47, 0.16);
}
.service-advocate-device-laptop-screen {
  display: grid;
  min-height: 120px;
  padding: 15px 17px;
  border-radius: 3px;
  background:
    linear-gradient(90deg, rgba(255, 255, 255, 0.92), rgba(246, 249, 247, 0.94)),
    var(--service-advocate-surface);
}
.service-advocate-device-nav {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 9px;
  color: var(--service-advocate-text-muted);
  font-size: 7px;
  line-height: 1;
  font-weight: 800;
  text-transform: uppercase;
}
.service-advocate-device-mark {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: var(--service-advocate-accent-dark);
}
.service-advocate-device-mark::before {
  content: "";
  display: inline-block;
  width: 9px;
  height: 9px;
  border-radius: 3px;
  background: var(--service-advocate-accent);
}
.service-advocate-device-lines {
  display: grid;
  gap: 5px;
  align-content: center;
  max-width: 154px;
  margin-top: 18px;
  color: var(--service-advocate-text);
}
.service-advocate-device-headline {
  margin: 0;
  font-size: 15px;
  line-height: 1.05;
  font-weight: 850;
  letter-spacing: 0;
}
.service-advocate-device-copy {
  margin: 0;
  color: var(--service-advocate-text-muted);
  font-size: 7px;
  line-height: 1.5;
  font-weight: 600;
}
.service-advocate-device-cta {
  width: max-content;
  margin-top: 3px;
  padding: 6px 11px;
  border-radius: 5px;
  background: var(--service-advocate-accent);
  color: var(--service-advocate-accent-contrast);
  font-size: 7px;
  line-height: 1;
  font-weight: 850;
}
.service-advocate-device-base {
  position: absolute;
  left: -12px;
  right: -18px;
  bottom: -10px;
  height: 12px;
  border-radius: 0 0 18px 18px;
  background: linear-gradient(180deg, #d6dde5, #8f9aaa);
}
.service-advocate-device-phone {
  position: absolute;
  right: 4px;
  bottom: 8px;
  width: 68px;
  min-height: 120px;
  padding: 11px 7px 9px;
  border: 4px solid #0b1220;
  border-radius: 14px;
  background: #ffffff;
  box-shadow: 0 18px 32px rgba(7, 20, 47, 0.16);
}
.service-advocate-device-phone::before {
  content: "";
  position: absolute;
  top: -2px;
  left: 50%;
  width: 24px;
  height: 4px;
  transform: translateX(-50%);
  border-radius: 999px;
  background: #0b1220;
}
.service-advocate-device-speed {
  display: grid;
  width: 47px;
  height: 47px;
  margin: 19px auto 7px;
  place-items: center;
  border: 6px solid var(--service-advocate-accent);
  border-right-color: rgba(22, 163, 74, 0.2);
  border-radius: 999px;
  color: var(--service-advocate-text);
  font-size: 15px;
  font-weight: 850;
  line-height: 1;
}
.service-advocate-device-speed-label {
  display: block;
  color: var(--service-advocate-text-muted);
  font-size: 7px;
  line-height: 1.2;
  text-align: center;
  font-weight: 800;
}
.service-advocate-features {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: ${t.spacing.tileGap}px;
  padding: 0 ${t.spacing.shellPadding}px 18px;
}
.service-advocate-feature {
  --service-advocate-item-accent: var(--service-advocate-accent);
  --service-advocate-item-soft: var(--service-advocate-accent-soft);
  display: grid;
  min-width: 0;
  grid-template-columns: auto minmax(0, 1fr);
  align-items: center;
  gap: 16px;
  min-height: 104px;
  padding: 18px 20px;
  border: 1px solid var(--service-advocate-border);
  border-radius: ${t.radius.tile}px;
  background: rgba(255, 255, 255, 0.72);
  box-shadow: 0 8px 20px rgba(7, 20, 47, 0.035);
}
.service-advocate-feature-icon {
  display: inline-flex;
  width: ${t.sizes.icon}px;
  height: ${t.sizes.icon}px;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border-radius: ${t.radius.icon}px;
  background: var(--service-advocate-item-soft);
  color: var(--service-advocate-item-accent);
}
.service-advocate-icon-node,
.service-advocate-feature-icon svg,
.service-advocate-action-icon svg,
.service-advocate-check-icon svg {
  display: block;
}
.service-advocate-feature-icon img {
  display: block;
  width: 52%;
  height: 52%;
  object-fit: contain;
}
.service-advocate-feature-icon .service-advocate-icon-node {
  width: 30px;
  height: 30px;
}
.service-advocate-feature-copy {
  min-width: 0;
}
.service-advocate-feature-title {
  margin: 0;
  color: var(--service-advocate-text);
  font-size: ${t.typography.tileTitle.fontSize}px;
  line-height: ${t.typography.tileTitle.lineHeight};
  font-weight: ${t.typography.tileTitle.fontWeight};
  letter-spacing: ${t.typography.tileTitle.letterSpacing};
  overflow-wrap: anywhere;
}
.service-advocate-feature-description {
  margin: 6px 0 0;
  color: var(--service-advocate-text-soft);
  font-size: ${t.typography.tileText.fontSize}px;
  line-height: ${t.typography.tileText.lineHeight};
  font-weight: ${t.typography.tileText.fontWeight};
  letter-spacing: ${t.typography.tileText.letterSpacing};
  overflow-wrap: anywhere;
}
.service-advocate-proof-grid {
  display: grid;
  grid-template-columns: minmax(220px, 0.95fr) minmax(280px, 1.42fr) minmax(220px, 0.82fr);
  gap: ${t.spacing.proofGap}px;
  margin: 0 ${t.spacing.shellPadding}px;
  overflow: hidden;
  border: 1px solid var(--service-advocate-border);
  border-radius: ${t.radius.tile}px;
  background: rgba(255, 255, 255, 0.78);
}
.service-advocate-proof-panel {
  min-width: 0;
  padding: 18px 22px;
}
.service-advocate-proof-panel + .service-advocate-proof-panel {
  border-left: 1px solid var(--service-advocate-border);
}
.service-advocate-review {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  align-items: center;
  gap: 16px;
  color: inherit;
  text-decoration: none;
}
.service-advocate-review:focus-visible,
.service-advocate-action:focus-visible {
  outline: 3px solid color-mix(in srgb, var(--service-advocate-accent) 34%, transparent);
  outline-offset: 4px;
}
.service-advocate-google-logo {
  display: inline-flex;
  width: 62px;
  height: 62px;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
}
.service-advocate-google-logo svg {
  display: block;
  width: 100%;
  height: 100%;
}
.service-advocate-proof-logo-image {
  display: block;
  width: 62px;
  max-width: 100%;
  height: auto;
}
.service-advocate-stars {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: ${t.colors.gold};
}
.service-advocate-stars svg {
  display: block;
  width: 20px;
  height: 20px;
  fill: currentColor;
  stroke: currentColor;
}
.service-advocate-rating {
  margin-left: 7px;
  color: var(--service-advocate-text);
  font-size: 15px;
  line-height: 1;
  font-weight: 800;
}
.service-advocate-proof-title {
  margin: 9px 0 0;
  color: var(--service-advocate-text);
  font-size: ${t.typography.proofTitle.fontSize}px;
  line-height: ${t.typography.proofTitle.lineHeight};
  font-weight: ${t.typography.proofTitle.fontWeight};
  letter-spacing: ${t.typography.proofTitle.letterSpacing};
  overflow-wrap: anywhere;
}
.service-advocate-proof-description {
  margin: 4px 0 0;
  color: var(--service-advocate-text-muted);
  font-size: ${t.typography.proofText.fontSize}px;
  line-height: ${t.typography.proofText.lineHeight};
  font-weight: ${t.typography.proofText.fontWeight};
  letter-spacing: ${t.typography.proofText.letterSpacing};
  overflow-wrap: anywhere;
}
.service-advocate-testimonial {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  align-items: center;
  gap: 16px;
  min-height: 78px;
}
.service-advocate-avatar {
  display: inline-flex;
  width: ${t.sizes.avatar}px;
  height: ${t.sizes.avatar}px;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border-radius: ${t.radius.avatar}px;
  background:
    linear-gradient(135deg, color-mix(in srgb, var(--service-advocate-accent) 28%, #ffffff), #f6d4c7);
  color: var(--service-advocate-text);
  font-size: 15px;
  line-height: 1;
  font-weight: 850;
}
.service-advocate-avatar img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.service-advocate-quote {
  margin: 0;
  color: var(--service-advocate-text-soft);
  font-size: ${t.typography.proofText.fontSize}px;
  line-height: ${t.typography.proofText.lineHeight};
  font-weight: 650;
  font-style: italic;
  letter-spacing: ${t.typography.proofText.letterSpacing};
  overflow-wrap: anywhere;
}
.service-advocate-author {
  margin: 6px 0 0;
  color: var(--service-advocate-text-muted);
  font-size: ${t.typography.proofText.fontSize}px;
  line-height: 1.35;
  font-weight: 600;
  letter-spacing: 0;
  overflow-wrap: anywhere;
}
.service-advocate-checklist {
  display: grid;
  gap: 8px;
  align-content: center;
  min-height: 78px;
  margin: 0;
  padding: 0;
  list-style: none;
}
.service-advocate-check {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  align-items: center;
  gap: 10px;
  color: var(--service-advocate-text-soft);
  font-size: ${t.typography.proofText.fontSize}px;
  line-height: ${t.typography.proofText.lineHeight};
  font-weight: 650;
  letter-spacing: 0;
  overflow-wrap: anywhere;
}
.service-advocate-check-icon {
  display: inline-flex;
  width: 19px;
  height: 19px;
  align-items: center;
  justify-content: center;
  color: var(--service-advocate-accent);
}
.service-advocate-actions {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 1px minmax(0, 1fr);
  align-items: center;
  gap: ${t.spacing.actionGap}px;
  margin-top: 0;
  padding: 16px ${t.spacing.shellPadding}px 20px;
  border-top: 1px solid var(--service-advocate-border);
}
.service-advocate-action-divider {
  display: block;
  width: 1px;
  height: 44px;
  background: var(--service-advocate-border);
}
.service-advocate-action-wrap {
  display: flex;
  min-width: 0;
  justify-content: center;
}
.service-advocate-action {
  display: inline-flex;
  min-width: min(100%, 300px);
  min-height: ${t.sizes.actionMinHeight}px;
  align-items: center;
  justify-content: center;
  gap: 11px;
  border-radius: ${t.radius.button}px;
  color: var(--service-advocate-accent-dark);
  font-size: ${t.typography.action.fontSize}px;
  line-height: ${t.typography.action.lineHeight};
  font-weight: ${t.typography.action.fontWeight};
  letter-spacing: ${t.typography.action.letterSpacing};
  text-align: center;
  text-decoration: none;
  overflow-wrap: anywhere;
}
.service-advocate-action--primary {
  padding: 13px 22px;
  background: linear-gradient(180deg, var(--service-advocate-accent), var(--service-advocate-accent-dark));
  color: var(--service-advocate-accent-contrast);
  box-shadow: inset 0 1px 0 rgba(255,255,255,0.26), var(--service-advocate-soft-shadow);
}
.service-advocate-action--secondary {
  min-width: 0;
  padding: 12px 6px;
  background: transparent;
}
.service-advocate-action-icon {
  display: inline-flex;
  width: 22px;
  height: 22px;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
}
.service-advocate-action-icon img {
  display: block;
  width: 20px;
  height: 20px;
  object-fit: contain;
}
@media (max-width: 980px) {
  .service-advocate-top {
    grid-template-columns: minmax(0, 1fr);
  }
  .service-advocate-content {
    max-width: none;
  }
  .service-advocate-media {
    justify-content: center;
    min-height: 176px;
  }
  .service-advocate-title {
    font-size: ${t.typography.title.tabletFontSize}px;
  }
  .service-advocate-description {
    font-size: ${t.typography.description.tabletFontSize}px;
  }
  .service-advocate-features {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .service-advocate-feature:last-child:nth-child(odd) {
    grid-column: 1 / -1;
  }
  .service-advocate-proof-grid {
    grid-template-columns: minmax(0, 1fr);
  }
  .service-advocate-proof-panel + .service-advocate-proof-panel {
    border-top: 1px solid var(--service-advocate-border);
    border-left: 0;
  }
}
@media (max-width: 760px) {
  .service-advocate {
    padding: ${t.spacing.sectionY - 18}px 14px;
  }
  .service-advocate-top {
    gap: 16px;
    padding: ${t.spacing.shellPaddingMobile}px ${t.spacing.shellPaddingMobile}px 18px;
  }
  .service-advocate-eyebrow {
    margin-bottom: 14px;
    font-size: 12px;
  }
  .service-advocate-eyebrow::before {
    width: 28px;
  }
  .service-advocate-title {
    font-size: ${t.typography.title.mobileFontSize}px;
  }
  .service-advocate-description {
    margin-top: 12px;
    font-size: ${t.typography.description.mobileFontSize}px;
  }
  .service-advocate-media {
    min-height: 162px;
    justify-content: center;
  }
  .service-advocate-device {
    transform: scale(0.82);
    transform-origin: center;
    margin: -18px 0 -28px;
  }
  .service-advocate-features {
    grid-template-columns: minmax(0, 1fr);
    gap: 12px;
    padding: 0 ${t.spacing.shellPaddingMobile}px 16px;
  }
  .service-advocate-feature,
  .service-advocate-feature:last-child:nth-child(odd) {
    grid-column: auto;
  }
  .service-advocate-feature {
    min-height: 0;
    gap: 15px;
    padding: 18px;
  }
  .service-advocate-feature-icon {
    width: 58px;
    height: 58px;
  }
  .service-advocate-feature-title {
    font-size: ${t.typography.tileTitle.mobileFontSize}px;
  }
  .service-advocate-feature-description {
    font-size: ${t.typography.tileText.mobileFontSize}px;
  }
  .service-advocate-proof-grid {
    margin: 0 ${t.spacing.shellPaddingMobile}px;
  }
  .service-advocate-proof-panel {
    padding: 20px 18px;
  }
  .service-advocate-review,
  .service-advocate-testimonial {
    grid-template-columns: minmax(0, 1fr);
    justify-items: start;
    gap: 14px;
  }
  .service-advocate-proof-title {
    font-size: ${t.typography.proofTitle.mobileFontSize}px;
  }
  .service-advocate-proof-description,
  .service-advocate-quote,
  .service-advocate-author,
  .service-advocate-check {
    font-size: ${t.typography.proofText.mobileFontSize}px;
  }
  .service-advocate-actions {
    grid-template-columns: minmax(0, 1fr);
    gap: 10px;
    padding: 18px ${t.spacing.shellPaddingMobile}px 20px;
  }
  .service-advocate-action-divider {
    display: none;
  }
  .service-advocate-action {
    width: 100%;
    min-width: 0;
    font-size: ${t.typography.action.mobileFontSize}px;
  }
}
@media (max-width: 430px) {
  .service-advocate-feature {
    grid-template-columns: minmax(0, 1fr);
  }
  .service-advocate-stars svg {
    width: 19px;
    height: 19px;
  }
  .service-advocate-rating {
    display: inline-block;
    margin-left: 6px;
    font-size: 15px;
  }
}
`;

const joinClassNames = (...classNames: Array<string | false | null | undefined>) =>
    classNames.filter(Boolean).join(' ');

const toCssSize = (value?: number | string) => {
    if (typeof value === 'number') {
        return `${value}px`;
    }

    return value;
};

const kebabCase = (value: string) =>
    value.replace(/[A-Z]/g, (letter) => `-${letter.toLowerCase()}`);

function isIconToken(icon: unknown): icon is ServiceAdvocateIconToken {
    return typeof icon === 'string' && iconTokenSet.has(icon);
}

function isRenderableIconComponent(icon: ServiceAdvocateIcon): icon is React.ElementType {
    return (
        typeof icon === 'function' ||
        (typeof icon === 'object' &&
            icon !== null &&
            '$$typeof' in icon &&
            !React.isValidElement(icon))
    );
}

function hasIconContent(icon: ServiceAdvocateIcon | undefined): boolean {
    return icon !== undefined && icon !== null && icon !== false;
}

function isChecklistObject(
    item: ServiceAdvocateChecklistItem,
): item is ServiceAdvocateChecklistObject {
    return (
        typeof item === 'object' && item !== null && !React.isValidElement(item) && 'text' in item
    );
}

function renderIcon(icon: ServiceAdvocateIcon | undefined, className: string, size: number) {
    if (!hasIconContent(icon)) return null;

    const iconProps = {
        size,
        strokeWidth: 2.25,
        className,
        'aria-hidden': true,
        focusable: false,
    };

    if (isIconToken(icon)) {
        const Icon = iconRegistry[icon];
        return <Icon {...iconProps} />;
    }

    if (React.isValidElement(icon)) {
        const element = icon as React.ReactElement<any>;
        return React.cloneElement(element, {
            ...iconProps,
            ...(element.props || {}),
        });
    }

    if (isRenderableIconComponent(icon)) {
        const Icon = icon as React.ElementType;
        return <Icon {...iconProps} />;
    }

    return <span className={className}>{icon}</span>;
}

function imageSourceValue(src?: ServiceAdvocateImage['src']) {
    if (!src) return undefined;
    if (typeof src === 'string') return src;
    return src.src;
}

function normalizeImage(
    image?: string | ServiceAdvocateImage | false,
    fallbackAlt = '',
): NormalizedImage | undefined {
    if (!image) return undefined;

    if (typeof image === 'string') {
        return {
            src: image,
            alt: fallbackAlt,
            loading: 'lazy',
            decoding: 'async',
            decorative: !fallbackAlt,
        };
    }

    const src = imageSourceValue(image.src) || image.url || image.original || image.thumbnail;

    if (!src) return undefined;

    const alt = image.decorative ? '' : image.alt || fallbackAlt;

    return {
        src,
        alt,
        title: image.title,
        width: image.width || (typeof image.src === 'object' ? image.src.width : undefined),
        height: image.height || (typeof image.src === 'object' ? image.src.height : undefined),
        objectPosition: image.objectPosition,
        loading: image.loading || 'lazy',
        decoding: image.decoding || 'async',
        decorative: image.decorative || !alt,
    };
}

function isImageLike(value: unknown): value is ServiceAdvocateImage {
    return (
        typeof value === 'object' &&
        value !== null &&
        !React.isValidElement(value) &&
        ('src' in value || 'url' in value || 'original' in value || 'thumbnail' in value)
    );
}

function toColorStyles(colors?: ServiceAdvocateColors): ServiceAdvocateCssVars {
    if (!colors) return {};

    return Object.entries(colors).reduce<ServiceAdvocateCssVars>((style, [token, value]) => {
        if (value) {
            style[`--service-advocate-${kebabCase(token)}`] = value;
        }

        return style;
    }, {});
}

function GoogleLogo() {
    return (
        <span className="service-advocate-google-logo" aria-hidden="true">
            <svg viewBox="0 0 48 48" role="img">
                <path
                    fill={t.colors.googleRed}
                    d="M24 9.5c3.54 0 6.73 1.22 9.24 3.61l6.91-6.91C35.91 2.44 30.37 0 24 0 14.82 0 6.73 5.15 2.69 12.68l8.06 6.26C12.7 13.16 17.9 9.5 24 9.5z"
                />
                <path
                    fill={t.colors.googleBlue}
                    d="M46.14 24.5c0-1.64-.15-3.21-.43-4.72H24v9h12.44c-.54 2.91-2.18 5.38-4.64 7.05l7.19 5.59C43.98 36.91 46.14 31.23 46.14 24.5z"
                />
                <path
                    fill={t.colors.googleYellow}
                    d="M10.75 28.94A14.5 14.5 0 019.5 24c0-1.72.3-3.37.84-4.94l-8.06-6.26A23.93 23.93 0 000 24c0 3.87.93 7.53 2.58 10.74l8.17-5.8z"
                />
                <path
                    fill={t.colors.googleGreen}
                    d="M24 48c6.48 0 11.92-2.13 15.9-5.79l-7.19-5.59c-2 1.35-4.56 2.15-8.71 2.15-6.1 0-11.3-3.66-13.25-8.84l-8.17 5.8C6.73 42.85 14.82 48 24 48z"
                />
            </svg>
        </span>
    );
}

function StarRating({ count = 5 }: { count?: number }) {
    const starCount = Math.max(0, Math.min(5, Math.round(count)));

    if (!starCount) return null;

    return (
        <span className="service-advocate-stars" aria-hidden="true">
            {Array.from({ length: starCount }).map((_, index) => (
                <Star key={index} size={24} strokeWidth={0} />
            ))}
        </span>
    );
}

function DefaultMedia() {
    return (
        <div className="service-advocate-device" aria-hidden="true">
            <div className="service-advocate-device-laptop">
                <div className="service-advocate-device-laptop-screen">
                    <div className="service-advocate-device-nav">
                        <span className="service-advocate-device-mark">Local site</span>
                        <span>Home Services Reviews</span>
                    </div>
                    <div className="service-advocate-device-lines">
                        <p className="service-advocate-device-headline">
                            Local service.
                            <br />
                            Real results.
                        </p>
                        <p className="service-advocate-device-copy">
                            Built to turn local search into enquiries.
                        </p>
                        <span className="service-advocate-device-cta">Get a free quote</span>
                    </div>
                </div>
                <span className="service-advocate-device-base" />
            </div>
            <div className="service-advocate-device-phone">
                <span className="service-advocate-device-speed">98</span>
                <span className="service-advocate-device-speed-label">Page speed</span>
            </div>
        </div>
    );
}

function renderImageIcon(iconSrc?: string, iconAlt?: string) {
    if (!iconSrc) return null;

    return <img src={iconSrc} alt={iconAlt || ''} loading="lazy" decoding="async" />;
}

function FeatureCard({ feature, index }: { feature: ServiceAdvocateFeature; index: number }) {
    const icon = renderImageIcon(feature.iconSrc, feature.iconAlt);
    const style: ServiceAdvocateCssVars = {
        '--service-advocate-item-accent':
            feature.accentColor || feature.accentColour || 'var(--service-advocate-accent)',
        '--service-advocate-item-soft':
            feature.accentSoftColor ||
            feature.accentSoftColour ||
            'var(--service-advocate-accent-soft)',
    };

    return (
        <article className="service-advocate-feature" style={style}>
            <span className="service-advocate-feature-icon">
                {icon || renderIcon(feature.icon, 'service-advocate-icon-node', 30)}
            </span>
            <div className="service-advocate-feature-copy">
                <h3 className="service-advocate-feature-title">{feature.title}</h3>
                {feature.description && (
                    <p className="service-advocate-feature-description">{feature.description}</p>
                )}
            </div>
        </article>
    );
}

function ProofLogo({ logo }: { logo?: ServiceAdvocateProofLogo }) {
    if (logo === false) return null;
    if (logo === undefined || logo === 'google') return <GoogleLogo />;

    if (isImageLike(logo)) {
        const image = normalizeImage(logo, '');
        if (!image) return null;

        return (
            <img
                className="service-advocate-proof-logo-image"
                src={image.src}
                alt={image.alt}
                title={image.title}
                width={image.width}
                height={image.height}
                loading={image.loading}
                decoding={image.decoding}
                aria-hidden={image.decorative ? true : undefined}
            />
        );
    }

    return <>{logo}</>;
}

function ProofPanel({
    proof,
    LinkComponent,
}: {
    proof: ServiceAdvocateProof;
    LinkComponent?: ServiceAdvocateLinkComponent;
}) {
    const content = (
        <>
            <ProofLogo logo={proof.logo} />
            <div className="service-advocate-review-copy">
                {((proof.showStars ?? true) || proof.rating) && (
                    <div>
                        {(proof.showStars ?? true) && <StarRating count={proof.starCount} />}
                        {proof.rating && (
                            <span className="service-advocate-rating">{proof.rating}</span>
                        )}
                    </div>
                )}
                {proof.title && <p className="service-advocate-proof-title">{proof.title}</p>}
                {proof.description && (
                    <p className="service-advocate-proof-description">{proof.description}</p>
                )}
            </div>
        </>
    );

    if (proof.href) {
        const className = 'service-advocate-review';

        if (LinkComponent) {
            return (
                <LinkComponent
                    href={proof.href}
                    className={className}
                    aria-label={proof.ariaLabel}
                    title={proof.titleAttribute}
                    target={proof.target}
                    rel={proof.rel}
                >
                    {content}
                </LinkComponent>
            );
        }

        return (
            <a
                href={proof.href}
                className={className}
                aria-label={proof.ariaLabel}
                title={proof.titleAttribute}
                target={proof.target}
                rel={proof.rel}
            >
                {content}
            </a>
        );
    }

    return <div className="service-advocate-review">{content}</div>;
}

function initialsFromName(name?: React.ReactNode) {
    if (typeof name !== 'string') return 'SA';

    const initials = name
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0])
        .join('');

    return initials || 'SA';
}

function TestimonialPanel({ testimonial }: { testimonial: ServiceAdvocateTestimonial }) {
    const avatar = normalizeImage(testimonial.avatar, testimonial.avatarAlt || '');

    return (
        <div className="service-advocate-testimonial">
            {testimonial.avatar !== false && (
                <span className="service-advocate-avatar">
                    {avatar ? (
                        <img
                            src={avatar.src}
                            alt={avatar.alt}
                            title={avatar.title}
                            width={avatar.width}
                            height={avatar.height}
                            loading={avatar.loading}
                            decoding={avatar.decoding}
                            aria-hidden={avatar.decorative ? true : undefined}
                        />
                    ) : (
                        initialsFromName(testimonial.authorName)
                    )}
                </span>
            )}
            <div>
                {testimonial.quote && (
                    <blockquote className="service-advocate-quote">
                        "{testimonial.quote}"
                    </blockquote>
                )}
                {(testimonial.authorName || testimonial.authorRole) && (
                    <p className="service-advocate-author">
                        {testimonial.authorName}
                        {testimonial.authorName && testimonial.authorRole ? ', ' : null}
                        {testimonial.authorRole}
                    </p>
                )}
            </div>
        </div>
    );
}

function ChecklistPanel({ checklist }: { checklist: ServiceAdvocateChecklistItem[] }) {
    return (
        <ul className="service-advocate-checklist">
            {checklist.map((item, index) => {
                const objectItem = isChecklistObject(item) ? item : undefined;
                const text: React.ReactNode = objectItem
                    ? objectItem.text
                    : (item as React.ReactNode);
                const icon = objectItem?.icon || 'checkCircle';
                const key = objectItem?.id ?? index;

                return (
                    <li className="service-advocate-check" key={key}>
                        <span className="service-advocate-check-icon">
                            {renderIcon(icon, 'service-advocate-icon-node', 19)}
                        </span>
                        <span>{text}</span>
                    </li>
                );
            })}
        </ul>
    );
}

function ActionLink({
    action,
    variant,
    LinkComponent,
}: {
    action: ServiceAdvocateAction;
    variant: 'primary' | 'secondary';
    LinkComponent?: ServiceAdvocateLinkComponent;
}) {
    const className = joinClassNames(
        'service-advocate-action',
        `service-advocate-action--${variant}`,
    );
    const icon = renderImageIcon(action.iconSrc, action.iconAlt);
    const content = (
        <>
            <span>{action.label}</span>
            {(icon || action.icon) && (
                <span className="service-advocate-action-icon">
                    {icon || renderIcon(action.icon, 'service-advocate-icon-node', 22)}
                </span>
            )}
        </>
    );

    if (LinkComponent) {
        return (
            <LinkComponent
                href={action.href}
                className={className}
                aria-label={action.ariaLabel}
                title={action.title}
                target={action.target}
                rel={action.rel}
            >
                {content}
            </LinkComponent>
        );
    }

    return (
        <a
            href={action.href}
            className={className}
            aria-label={action.ariaLabel}
            title={action.title}
            target={action.target}
            rel={action.rel}
        >
            {content}
        </a>
    );
}

function renderMedia(media: ServiceAdvocateMedia | undefined) {
    if (media === false) return null;
    if (media === undefined || media === null) {
        return <DefaultMedia />;
    }

    if (isImageLike(media)) {
        const image = normalizeImage(media, media.alt || '');

        if (!image) return null;

        const style: ServiceAdvocateCssVars = {
            '--service-advocate-media-position': image.objectPosition,
        };

        return (
            <img
                src={image.src}
                alt={image.alt}
                title={image.title}
                width={image.width}
                height={image.height}
                loading={image.loading}
                decoding={image.decoding}
                aria-hidden={image.decorative ? true : undefined}
                style={style}
            />
        );
    }

    return <>{media}</>;
}

export default function ServiceAdvocate({
    eyebrow = t.defaults.eyebrow,
    title = t.defaults.title,
    description = t.defaults.description,
    features,
    media,
    proof,
    testimonial,
    checklist,
    primaryAction,
    secondaryAction,
    LinkComponent,
    Link,
    titleComponent: TitleComponent = 'h2',
    accentColor,
    accentColour,
    accentDarkColor,
    accentDarkColour,
    accentSoftColor,
    accentSoftColour,
    backgroundColor,
    backgroundColour,
    surfaceColor,
    surfaceColour,
    textColor,
    textColour,
    textSoftColor,
    textSoftColour,
    textMutedColor,
    textMutedColour,
    colors,
    colours,
    maxWidth,
    id,
    className,
    style,
    ariaLabel,
}: ServiceAdvocateProps) {
    const resolvedLink = LinkComponent || Link;
    const resolvedFeatures: ServiceAdvocateFeature[] =
        features && features.length
            ? features
            : t.defaults.features.map((feature) => ({ ...feature }));
    const resolvedProof = proof === false ? false : { ...t.defaults.proof, ...(proof || {}) };
    const resolvedTestimonial =
        testimonial === false ? false : { ...t.defaults.testimonial, ...(testimonial || {}) };
    const resolvedChecklist =
        checklist === false
            ? false
            : checklist && checklist.length
              ? checklist
              : [...t.defaults.checklist];
    const resolvedPrimaryAction =
        primaryAction === false ? false : { ...t.defaults.primaryAction, ...(primaryAction || {}) };
    const resolvedSecondaryAction =
        secondaryAction === false
            ? false
            : { ...t.defaults.secondaryAction, ...(secondaryAction || {}) };
    const renderedMedia = renderMedia(media);
    const hasMedia = renderedMedia !== null;
    const hasProofGrid = Boolean(resolvedProof || resolvedTestimonial || resolvedChecklist);
    const hasActions = Boolean(resolvedPrimaryAction || resolvedSecondaryAction);
    const rootStyle: ServiceAdvocateCssVars = {
        ...toColorStyles(colours || colors),
        '--service-advocate-bg':
            backgroundColour || backgroundColor || colours?.background || colors?.background,
        '--service-advocate-surface':
            surfaceColour || surfaceColor || colours?.surface || colors?.surface,
        '--service-advocate-text': textColour || textColor || colours?.text || colors?.text,
        '--service-advocate-text-soft':
            textSoftColour || textSoftColor || colours?.textSoft || colors?.textSoft,
        '--service-advocate-text-muted':
            textMutedColour || textMutedColor || colours?.textMuted || colors?.textMuted,
        '--service-advocate-accent':
            accentColour || accentColor || colours?.accent || colors?.accent,
        '--service-advocate-accent-dark':
            accentDarkColour || accentDarkColor || colours?.accentDark || colors?.accentDark,
        '--service-advocate-accent-soft':
            accentSoftColour || accentSoftColor || colours?.accentSoft || colors?.accentSoft,
        '--service-advocate-max-width': toCssSize(maxWidth || t.sizes.maxWidth),
        ...style,
    };

    return (
        <section
            id={id}
            className={joinClassNames('service-advocate', className)}
            style={rootStyle}
            aria-label={ariaLabel}
            data-has-media={hasMedia ? 'true' : 'false'}
        >
            <style>{serviceAdvocateCss}</style>
            <div className="service-advocate-shell">
                <div className="service-advocate-top">
                    <div className="service-advocate-content">
                        {eyebrow && <p className="service-advocate-eyebrow">{eyebrow}</p>}
                        {title && (
                            <TitleComponent className="service-advocate-title">
                                {title}
                            </TitleComponent>
                        )}
                        {description && (
                            <p className="service-advocate-description">{description}</p>
                        )}
                    </div>
                    {hasMedia && <div className="service-advocate-media">{renderedMedia}</div>}
                </div>

                {resolvedFeatures.length > 0 && (
                    <div className="service-advocate-features">
                        {resolvedFeatures.map((feature, index) => (
                            <FeatureCard
                                feature={feature}
                                index={index}
                                key={feature.id ?? index}
                            />
                        ))}
                    </div>
                )}

                {hasProofGrid && (
                    <div className="service-advocate-proof-grid">
                        {resolvedProof && (
                            <div className="service-advocate-proof-panel">
                                <ProofPanel proof={resolvedProof} LinkComponent={resolvedLink} />
                            </div>
                        )}
                        {resolvedTestimonial && (
                            <div className="service-advocate-proof-panel">
                                <TestimonialPanel testimonial={resolvedTestimonial} />
                            </div>
                        )}
                        {resolvedChecklist && resolvedChecklist.length > 0 && (
                            <div className="service-advocate-proof-panel">
                                <ChecklistPanel checklist={resolvedChecklist} />
                            </div>
                        )}
                    </div>
                )}

                {hasActions && (
                    <div className="service-advocate-actions">
                        {resolvedPrimaryAction && (
                            <div className="service-advocate-action-wrap">
                                <ActionLink
                                    action={resolvedPrimaryAction}
                                    variant="primary"
                                    LinkComponent={resolvedLink}
                                />
                            </div>
                        )}
                        {resolvedPrimaryAction && resolvedSecondaryAction && (
                            <span className="service-advocate-action-divider" />
                        )}
                        {resolvedSecondaryAction && (
                            <div className="service-advocate-action-wrap">
                                <ActionLink
                                    action={resolvedSecondaryAction}
                                    variant="secondary"
                                    LinkComponent={resolvedLink}
                                />
                            </div>
                        )}
                    </div>
                )}
            </div>
        </section>
    );
}

export { serviceAdvocateIconTokens, serviceAdvocateTokens } from './ServiceAdvocate.tokens';
export type { ServiceAdvocateColorToken, ServiceAdvocateIconToken } from './ServiceAdvocate.tokens';
